// Native PDF hand-off for the simple-viewer path.
//
// Android WebView has never shipped a PDF renderer, and on iOS WKWebView an iframed PDF renders
// only its first page and will not scroll. So on device we stop trying to paint the PDF ourselves
// and give it to the OS viewer instead, keeping the in-app pdf.js canvas for web.
//
// The decision lives here (and is called from attachmentOpener) rather than inside SimplePdfViewer
// on purpose: four content modals mount that viewer as an INLINE PREVIEW inside a live form, and
// ModalUploadQuestionPdf gates the rest of its form on the page count the viewer emits. Handing
// those off to the OS would blank the preview and leave the form permanently gated.

import { MimeTypesEnum } from "@/enums/global";
import { getCleanFileName } from "@/methods/utils";
import type { AttachmentPdfVariant } from "@/types/attachment";
import { FileTransfer } from "@capacitor/file-transfer";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener } from "@capawesome-team/capacitor-file-opener";

const PDF_CACHE_PREFIX = "pdf-view-";

/**
 * Pure decision — unit-tested in isolation, matching the style of attachmentOpener's resolvers.
 *
 * Deliberately scoped to the "simple" variant: "flip" renders a Heyzine HTML flipbook, which is a
 * normal web page in an iframe and works fine on device.
 */
export function shouldOpenPdfNatively(
  isNativePlatform: boolean,
  pdfVariant: AttachmentPdfVariant,
  forceInAppPdf?: boolean,
): boolean {
  return isNativePlatform && pdfVariant === "simple" && !forceInAppPdf;
}

/** Cache-relative file name. Written flat into the Cache root so no mkdir is needed. */
export function getPdfCacheFileName(name?: string): string {
  const base = name?.trim() ? getCleanFileName(name.trim()) : "document";
  const withoutExtension = base.replace(/\.pdf$/i, "");
  return `${PDF_CACHE_PREFIX}${withoutExtension}.pdf`;
}

/** Strip the `data:...;base64,` prefix — Filesystem.writeFile wants the payload alone. */
export function getBase64Payload(source: string): string {
  return source.slice(source.indexOf(",") + 1);
}

/**
 * Materialise the PDF into the cache directory and hand it to the OS viewer.
 *
 * Directory.Cache, not Directory.Data: this is a throwaway copy for viewing, and the OS must be
 * free to evict it — Directory.Data is where the offline-download feature keeps files it owns.
 * Capacitor's stock `file_paths.xml` already exposes `<cache-path path="." />` and the plugin
 * resolves `${applicationId}.fileprovider`, which the stock manifest declares, so this needs no
 * native configuration.
 *
 * `source` must already be a ready-to-fetch URL or data URI — the caller applies getAzureUrl
 * exactly once, and applying it again here would duplicate the SAS token and break the URL.
 *
 * Returns false rather than throwing so the caller can fall back to the in-app viewer.
 */
export async function openPdfWithNativeViewer(source: string, name?: string): Promise<boolean> {
  const path = getPdfCacheFileName(name);

  try {
    if (source.startsWith("data:")) {
      await Filesystem.writeFile({
        path,
        directory: Directory.Cache,
        data: getBase64Payload(source),
      });
    } else {
      const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
      await FileTransfer.downloadFile({ url: source, path: uri });
    }

    const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
    await FileOpener.openFile({ path: uri, mimeType: MimeTypesEnum.PDF });
    return true;
  } catch (error) {
    console.error("Native PDF hand-off failed, falling back to the in-app viewer:", error);
    return false;
  }
}
